import CookiesMgr from 'coreClasses/cookies/CookiesMgr';
import Manager from 'coreClasses/Manager';
import {selectOpenedPopupIds, receiveOpenPopups} from 'coreStore/data/ui';

/**
 * Represents PopupsMgr
 */
class PopupsMgr extends Manager {
	private viewedPopupIds: string[] = [];

	/**
	 * Opens popup
	 * @param {String} popupId Popup id
	 */
	openPopup(popupId: string): void {
		if (!this.isPopupOpened(popupId)) {
			this.setOpenedPopupIds([...this.getOpenedPopupIds(), popupId]);
		}
	}

	/**
	 * Closes popup
	 * @param {String} popupId Popup id
	 */
	closePopup(popupId: string): void {
		if (this.isPopupOpened(popupId)) {
			const newPopupids = this.getOpenedPopupIds().filter((popupIdElem) => popupIdElem !== popupId);

			this.setOpenedPopupIds(newPopupids);
		}
	}

	/**
	 * Close all popups
	 */
	closeAllPopups(): void {
		this.getOpenedPopupIds().forEach((popupId) => this.closePopup(popupId));
	}

	/**
	 * Checks if popup is opened
	 * @param {String} popupId Popup id
	 * @returns {Boolean}
	 */
	isPopupOpened(popupId: string): boolean {
		return !!popupId && this.getOpenedPopupIds().includes(popupId);
	}

	/**
	 * Returns true if any popup is open in the page
	 * @returns {Boolean}
	 */
	isAnyPopupOpen(): boolean {
		return !!this.getOpenedPopupIds().length;
	}

	/**
	 * Retrieves opened popup id
	 * @returns {Array[String]}
	 */
	public getOpenedPopupIds(): string[] {
		const store = this.managerRepository.getStoreMgr().getStore();

		return selectOpenedPopupIds(store.getState());
	}

	/**
	 * Updates store with popup information
	 * @param {String} openedPopupIds Popup id to set in store
	 */
	private setOpenedPopupIds(openedPopupIds: string[]): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveOpenPopups(openedPopupIds));
	}

	/**
	 * Checks if a popup with the given ID has been viewed.
	 *
	 * @param {String} popupId - The ID of the popup to check.
	 * @param {Boolean} checkCookie - Whether to check the cookie for the popup ID.
	 * @returns {Boolean} `true` if the popup has been viewed, otherwise `false`.
	 */
	isViewedPopupId(popupId: string, checkCookie: boolean): boolean {
		if (this.viewedPopupIds.includes(popupId)) {
			return true;
		}

		if (checkCookie) {
			const cookie = CookiesMgr.getCookie(popupId);

			return !!cookie.getValue();
		}

		return false;
	}

	/**
	 * Set viewed popup id
	 * @param {String} viewedPopupId Viewed popup id
	 */
	setViewedPopupId(viewedPopupId: string): void {
		if (!this.viewedPopupIds.includes(viewedPopupId)) {
			this.viewedPopupIds.push(viewedPopupId);
		}
	}
}

export default PopupsMgr;
